const MIN_POLL_MS = 500
const MAX_POLL_MS = 10_000
const DEFAULT_POLL_MS = 1_000
export const MAX_CONSECUTIVE_POLL_FAILURES = 4
export const STATUS_POLL_REQUEST_TIMEOUT_MS = 15_000

type HeaderReader = {
  get: (name: string) => string | null
}

type ErrorWithResponseHeaders = {
  responseHeaders?: unknown
  headers?: unknown
  response?: {
    headers?: unknown
  }
}

export const clampPollDelay = (value: number | null | undefined): number => {
  if (!Number.isFinite(value)) return DEFAULT_POLL_MS

  return Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, Math.round(Number(value))))
}

export const transientPollDelay = (
  serverDelay: number | null | undefined,
  failures: number,
): number => Math.min(MAX_POLL_MS, clampPollDelay(serverDelay) * 2 ** Math.max(0, failures))

export const pollFailuresExhausted = (failures: number): boolean =>
  failures >= MAX_CONSECUTIVE_POLL_FAILURES

const retryAfterHeader = (error: unknown): string | null => {
  if (!error || typeof error !== "object") return null

  const candidate = error as ErrorWithResponseHeaders
  const headerCandidates = [
    candidate.responseHeaders,
    candidate.headers,
    candidate.response?.headers,
  ]
  const headers = headerCandidates.find((value): value is HeaderReader =>
    Boolean(value && typeof (value as HeaderReader).get === "function"),
  )

  return headers?.get("Retry-After")?.trim() || null
}

export const retryAfterPollDelay = (error: unknown, now = Date.now()): number | null => {
  const value = retryAfterHeader(error)

  if (!value) return null

  if (/^\d+$/.test(value)) {
    return clampPollDelay(Number(value) * 1_000)
  }

  const retryAt = Date.parse(value)

  if (!Number.isFinite(retryAt)) return null

  return clampPollDelay(Math.max(0, retryAt - now))
}

export const abortableDelay = (milliseconds: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Polling aborted", "AbortError"))

      return
    }

    const timeout = window.setTimeout(() => {
      signal.removeEventListener("abort", abort)
      resolve()
    }, clampPollDelay(milliseconds))
    const abort = () => {
      window.clearTimeout(timeout)
      signal.removeEventListener("abort", abort)
      reject(new DOMException("Polling aborted", "AbortError"))
    }
    signal.addEventListener("abort", abort, { once: true })
  })

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError"
